/**
 * Stedsnavnskilt for rutas to endepunkter.
 *
 * Skiltblått felt med hvit tekst og avrundede hjørner, uten stolper: skiltet
 * SVEVER over punktet. Stolper ville krevd at foten står nøyaktig på bakken,
 * og da blir skiltet et objekt i landskapet som må tåle å bli sett fra alle
 * kanter. Et svevende skilt er en ren kartmarkør og slipper det problemet.
 *
 * Hvorfor symbol-lag og ikke 3D-modell: skiltet skal leses «langt ute», på
 * vidvinkel. En 3D-modell i ekte meter blir noen få piksler på zoom 11 og
 * forsvinner. Et symbol-lag tegnes i skjermrommet, vender alltid mot
 * kameraet og holder lesbar størrelse uansett avstand og pitch – som et
 * kartografisk kartnål-skilt, ikke et objekt i landskapet.
 *
 * Med terreng på løfter MapLibre symboler til terrenghøyde av seg selv, så
 * skiltene står på bakken uten at vi gjør noe (i motsetning til custom
 * layers, jf. models.js og tunnel-portals.js).
 */

export const PLACE_SIGNS_LAYER_ID = 'place-signs';
const SOURCE_ID = 'place-signs';

/** Endepunktene. Plasseringen er rutas start og slutt. */
export const PLACE_SIGNS = [
  { name: 'Bærums Verk', lngLat: [10.49952, 59.942188] },
  { name: 'Aker Brygge', lngLat: [10.728631, 59.909917] },
];

// Bakgrunnsfarge på skiltet (DN mørk marineblå).
const FIELD = '#13264A';
const INK = '#ffffff';     // tekst

// DN-fonten Book20 (SharpGroteskBook-20). Canvas kan bare tegne med fonter
// som allerede er lastet i dokumentet, så vi laster den via FontFace API og
// venter på den før skiltene tegnes. Faller tilbake til system-sans hvis
// nedlastingen feiler.
const BOOK20_URL =
  'https://static1.dn.no/dn/static/assets/webfonts/nhstfonts/SharpGroteskBook-20.woff2';
const SIGN_FONT_FAMILY = 'Book20';
const FALLBACK_FAMILY = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

let signFontPromise = null;
/** Laster Book20 én gang og legger den til i document.fonts. */
function loadSignFont() {
  if (signFontPromise) return signFontPromise;
  if (typeof FontFace === 'undefined') {
    signFontPromise = Promise.resolve(false);
    return signFontPromise;
  }
  const face = new FontFace(SIGN_FONT_FAMILY, `url(${BOOK20_URL}) format('woff2')`);
  signFontPromise = face
    .load()
    .then((loaded) => {
      document.fonts.add(loaded);
      return true;
    })
    .catch(() => false); // fallback til system-font
  return signFontPromise;
}

/**
 * Skiltet forsvinner over dette zoom-nivået. Tett på er man «inne i» scenen,
 * og et skjermfestet skilt i den skalaen dekker mer enn det forklarer.
 */
export const SIGN_FADE_ZOOM = 16;

/**
 * Tegner ett skilt og returnerer ImageData klart for map.addImage().
 * pixelRatio 2 gjør at teksten holder seg skarp på retina.
 */
function signImage(text, ratio = 2) {
  // Dobbelt så stort som første utkast. Verdiene er i CSS-piksler før
  // ratio-oppskaleringen, som bare finnes for at teksten skal bli skarp.
  const pad = 20, radius = 10;
  const family = document.fonts?.check?.(`26px ${SIGN_FONT_FAMILY}`)
    ? `'${SIGN_FONT_FAMILY}', ${FALLBACK_FAMILY}`
    : FALLBACK_FAMILY;
  const font = `400 ${26 * ratio}px ${family}`;

  // Mål teksten først, så kan lerretet få riktig bredde.
  const probe = document.createElement('canvas').getContext('2d');
  probe.font = font;
  const textW = Math.ceil(probe.measureText(text).width);

  const w = textW + pad * 2 * ratio;
  const fieldH = 52 * ratio;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = fieldH;
  const ctx = canvas.getContext('2d');

  // Skiltfelt: heldekkende blått med avrundede hjørner, ingen ramme -
  // hvit tekst på blått bærer kontrasten alene.
  ctx.beginPath();
  ctx.roundRect(0, 0, w, fieldH, radius * ratio);
  ctx.fillStyle = FIELD;
  ctx.fill();

  ctx.font = font;
  ctx.fillStyle = INK;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, w / 2, fieldH / 2 + ratio);

  return ctx.getImageData(0, 0, w, fieldH);
}

/**
 * Legger skiltene inn i kartet.
 * @param {import('maplibre-gl').Map} map
 * @param {string} [beforeId] sett laget under dette laget
 */
export function addPlaceSigns(map, beforeId) {
  map.addSource(SOURCE_ID, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: PLACE_SIGNS.map(({ name, lngLat }) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: lngLat },
        properties: { name },
      })),
    },
  });

  // Tegn og legg inn skiltbildene. Vi venter på Med21 først, siden ulik
  // font gir ulik tekstbredde (og dermed ulik bildestørrelse) – da kan vi
  // ikke bruke updateImage. loadSignFont() feiler mykt til fallback-font,
  // så skiltene kommer uansett. Layer legges til uavhengig, så det finnes
  // selv om bildene kommer et øyeblikk senere.
  const addSignImages = () => {
    for (const { name } of PLACE_SIGNS) {
      if (map.hasImage(`sign-${name}`)) map.removeImage(`sign-${name}`);
      map.addImage(`sign-${name}`, signImage(name), { pixelRatio: 2 });
    }
    map.triggerRepaint();
  };
  loadSignFont().then(addSignImages);

  map.addLayer({
    id: PLACE_SIGNS_LAYER_ID,
    type: 'symbol',
    source: SOURCE_ID,
    layout: {
      'icon-image': ['concat', 'sign-', ['get', 'name']],
      // Skiltet svever over punktet i stedet for å stå på det.
      'icon-anchor': 'bottom',
      'icon-offset': [0, -28],
      // To skilt som aldri skal vike for annen tekst – de er hele poenget.
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      // Litt mindre på vidvinkel så de ikke dekker ruta, full størrelse tett på.
      'icon-size': ['interpolate', ['linear'], ['zoom'], 9, 0.55, 13, 0.8, 16, 1],
    },
    paint: {
      'icon-opacity': ['interpolate', ['linear'], ['zoom'],
        SIGN_FADE_ZOOM - 1, 1, SIGN_FADE_ZOOM, 0],
    },
  }, beforeId);
}
